import { useMemo } from 'react';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { TestExecutionField } from '@elementary/common-ui/services/gql/types';
import { SortDirection } from '@elementary/common-ui/components/Table/types';
import {
  mapUrlFiltersToGraphqlFilters,
  parseGraphqlFilterOptions,
} from '@elementary/common-ui/utils/graphqlFilters';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { distinctArrayBy } from '@elementary/common-ui/utils/common';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { hashArray } from '@elementary/common-ui/utils/hashing';
import {
  TEST_RUNS,
  TEST_RUNS_FILTER_OPTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import useGraphqlPagedQuery from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import { DEFAULT_DAYS_BACK, PAGE_SIZE } from '@elementary/common-ui/constants';
import {
  TEST_RUNS_TABLE_FILTERS,
  TEST_RUNS_TABLE_SORT,
} from '@elementary/common-ui/constants/urlParams';
import {
  parseApiTestRun,
  type ApiTestRunData,
} from '@elementary/common-ui/pages';
import { TestRun } from '@elementary/common-ui/types';
import {
  getTestRunsFilterOptionsQuery,
  getTestRunsTableDataQuery,
} from './api';
import {
  ApiTestRunsFilterOptionsResponse,
  ApiTestRunsTableDataPagedResponse,
} from './types';
import { COLUMN_TO_FIELD_MAP, FIELD_TO_COLUMN_MAP } from './consts';

const DEFAULT_SORT = {
  id: 'metadata.latest_run_time',
  direction: 'desc',
};

const useTestRuns = ({
  enabled = true,
  onError,
  search,
}: {
  enabled?: boolean;
  onError?: () => void;
  search?: string;
}) => {
  const envid = useSelectedEnv();

  const { selectedTreeNode, selectedTreeFolderNodes } = useSelectedTreeItem();
  const { urlFiltersDebounced } = useUrlFilters({
    urlKey: TEST_RUNS_TABLE_FILTERS,
  });
  const [searchParams] = useSearchParams();

  const hasSelectedNodes = useMemo(
    () => !!(selectedTreeFolderNodes?.length || selectedTreeNode?.id),
    [selectedTreeFolderNodes, selectedTreeNode],
  );

  const { selectedAssetIds, selectedTestIds } = useMemo(() => {
    const selectedAssetIds = [];
    const selectedTestIds = [];
    if (selectedTreeFolderNodes) {
      selectedTreeFolderNodes.forEach((node) => {
        if (node.resourceType !== 'test') {
          selectedAssetIds.push(node.id);
        } else {
          selectedTestIds.push(node.id);
        }
      });
    } else if (selectedTreeNode) {
      if (selectedTreeNode.resourceType !== 'test') {
        selectedAssetIds.push(selectedTreeNode.id);
      } else {
        selectedTestIds.push(selectedTreeNode.id);
      }
    }
    return { selectedAssetIds, selectedTestIds };
  }, [selectedTreeFolderNodes, selectedTreeNode]);

  const selectedIdsHash = useMemo(() => {
    const testsHash = hashArray(selectedTestIds);
    const assetsHash = hashArray(selectedAssetIds);
    return `${testsHash}-${assetsHash}`;
  }, [selectedTestIds, selectedAssetIds]);

  const urlSort: { id: string; direction: SortDirection } | null = useMemo(
    () =>
      JSON.parse(searchParams.get(TEST_RUNS_TABLE_SORT) || 'null') ||
      DEFAULT_SORT,
    [searchParams],
  );

  const testExecutionFilters = useMemo(
    () =>
      mapUrlFiltersToGraphqlFilters(urlFiltersDebounced, COLUMN_TO_FIELD_MAP),
    [urlFiltersDebounced],
  );

  const sort = useMemo(() => {
    const localUrlSort = urlSort || DEFAULT_SORT;
    const field = COLUMN_TO_FIELD_MAP[localUrlSort.id];
    if (!field) return undefined;
    return {
      field,
      direction: localUrlSort.direction.toUpperCase() as 'ASC' | 'DESC',
    };
  }, [urlSort]);

  const { data, isLoading, isFetching, isError, fetchNextPage, hasNextPage } =
    useGraphqlPagedQuery<ApiTestRunData, ApiTestRunsTableDataPagedResponse>({
      queryKey: [
        TEST_RUNS,
        envid,
        selectedIdsHash,
        DEFAULT_DAYS_BACK,
        urlFiltersDebounced,
        urlSort,
        search,
      ],
      query: getTestRunsTableDataQuery,
      variables: {
        testIds: selectedTestIds.length ? selectedTestIds : null,
        assetIds: selectedAssetIds.length ? selectedAssetIds : null,
        daysBack: DEFAULT_DAYS_BACK,
        testExecutionFilters,
        sort,
        search: search == '' ? null : search,
        limit: PAGE_SIZE,
      },
      select: (data) => data.latestTestExecutionsPaged,
      onError: onError,
      enabled: enabled && hasSelectedNodes,
      envId: envid,
    });

  const { data: filterOptionsData, isFetching: isFilterOptionsFetching } =
    useGraphqlQuery<ApiTestRunsFilterOptionsResponse>({
      queryKey: [
        TEST_RUNS_FILTER_OPTIONS,
        envid,
        selectedIdsHash,
        DEFAULT_DAYS_BACK,
      ],
      query: getTestRunsFilterOptionsQuery,
      variables: {
        testIds: selectedTestIds.length ? selectedTestIds : null,
        assetIds: selectedAssetIds.length ? selectedAssetIds : null,
        daysBack: DEFAULT_DAYS_BACK,
      },
      enabled: enabled && hasSelectedNodes,
      envId: envid,
    });

  const filterOptions = useMemo(
    () =>
      parseGraphqlFilterOptions<TestExecutionField>(
        filterOptionsData?.latestTestExecutionsFilterOptions || [],
        FIELD_TO_COLUMN_MAP,
      ),
    [filterOptionsData],
  );

  const tableData = useMemo<TestRun[]>(() => {
    const distinctApiTestRuns = distinctArrayBy(
      data,
      'execution.subTestUniqueId',
    );
    return distinctApiTestRuns.map(parseApiTestRun);
  }, [data]);

  return {
    tableData,
    filterOptions,
    isFetching: isLoading || isFetching,
    isFilterOptionsFetching,
    isError,
    fetchNextPage,
    hasNextPage,
  };
};

export default useTestRuns;
